import React, { useState, useEffect, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { AuthContext } from '../../context/AuthContext';
import axios from 'axios';

const UserRouteDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useContext(AuthContext);
  const [route, setRoute] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showInitialInfoForm, setShowInitialInfoForm] = useState(false);
  const [showFinalInfoForm, setShowFinalInfoForm] = useState(false);
  const [showUpdateForm, setShowUpdateForm] = useState(false);
  const [initialInfoData, setInitialInfoData] = useState({
    driverName: '',
    rut: '',
    truckLicensePlate: '',
    trailerLicensePlate: '',
    comments: ''
  });
  const [finalInfoData, setFinalInfoData] = useState({
    arrivalTime: '',
    arrivalDay: '',
    guides: '',
    sinister: '',
    comments: '',
    receptionBellPhoto: null
  });
  const [updateData, setUpdateData] = useState({
    updateType: 'in-transit',
    description: '',
    location: ''
  });
  const [showWaybillForm, setShowWaybillForm] = useState(false);
  const [waybillData, setWaybillData] = useState({
    additionalComments: '',
    photos: []
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (!authLoading && user) {
      fetchRoute();
    }
  }, [id, authLoading, user]);

  const fetchRoute = async () => {
    try {
      const res = await axios.get(`/api/routes/${id}`);
      setRoute(res.data);
      
      if (!user || !user._id) {
        setShowInitialInfoForm(false);
        setShowFinalInfoForm(false);
        setLoading(false);
        return;
      }
      
      // Helper function to normalize user ID - handles all possible formats
      const normalizeUserId = (userObj) => {
        if (!userObj) return null;
        // If it's already a string, return it
        if (typeof userObj === 'string') return userObj;
        // If it's an object with _id (populated user or ObjectId)
        if (userObj._id) {
          const id = userObj._id;
          return typeof id === 'string' ? id : id.toString();
        }
        // If it's an ObjectId or has toString method
        if (typeof userObj.toString === 'function') {
          return userObj.toString();
        }
        // Last resort
        return String(userObj);
      };
      
      // Check if current user is assigned to this route
      // Handle user._id - could be string or ObjectId
      let currentUserId = null;
      if (user?._id) {
        if (typeof user._id === 'string') {
          currentUserId = user._id;
        } else if (user._id._id) {
          // If it's a populated object
          currentUserId = typeof user._id._id === 'string' ? user._id._id : user._id._id.toString();
        } else {
          currentUserId = user._id.toString();
        }
      }
      
      // Handle assignedTo - could be populated object with _id or ObjectId
      let assignedUserId = null;
      if (res.data.assignedTo) {
        if (typeof res.data.assignedTo === 'string') {
          assignedUserId = res.data.assignedTo;
        } else if (res.data.assignedTo._id) {
          // Populated object
          assignedUserId = typeof res.data.assignedTo._id === 'string' 
            ? res.data.assignedTo._id 
            : res.data.assignedTo._id.toString();
        } else {
          // ObjectId
          assignedUserId = res.data.assignedTo.toString();
        }
      }
      
      const isAssignedToCurrentUser = currentUserId && assignedUserId && currentUserId === assignedUserId;
      
      // Show initial info form if route is pending and user is assigned and hasn't submitted initial info yet
      // This form allows the assigned user to submit: Driver's name, RUT, Patent tract, Cellular ramp patent, Comments
      if (isAssignedToCurrentUser && res.data.status === 'pending' && !res.data.initialInfo) {
        setShowInitialInfoForm(true);
        setShowFinalInfoForm(false);
      } else {
        setShowInitialInfoForm(false);
      }
      
      // Show final info form if route is in-progress and user is assigned and hasn't submitted final info yet
      if (isAssignedToCurrentUser && res.data.status === 'in-progress' && !res.data.finalInfo) {
        setShowFinalInfoForm(true);
      } else {
        setShowFinalInfoForm(false);
      }
    } catch (error) {
      console.error('Error fetching route:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitInitialInfo = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/user/routes/${id}/submit-initial-info`, initialInfoData);
      setShowInitialInfoForm(false);
      setInitialInfoData({ driverName: '', rut: '', truckLicensePlate: '', trailerLicensePlate: '', comments: '' });
      fetchRoute();
      alert('Información inicial enviada exitosamente. El estado de la ruta ha cambiado a "En Progreso".');
    } catch (error) {
      alert(error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Error al enviar información inicial');
    }
  };

  const handleFinalInfoPhotoChange = (e) => {
    setFinalInfoData({
      ...finalInfoData,
      receptionBellPhoto: e.target.files[0]
    });
  };

  const handleSubmitFinalInfo = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('arrivalTime', finalInfoData.arrivalTime);
      formData.append('arrivalDay', finalInfoData.arrivalDay);
      formData.append('guides', finalInfoData.guides);
      formData.append('sinister', finalInfoData.sinister || '');
      formData.append('comments', finalInfoData.comments || '');
      if (finalInfoData.receptionBellPhoto) {
        formData.append('receptionBellPhoto', finalInfoData.receptionBellPhoto);
      }

      await axios.post(`/api/user/routes/${id}/submit-final-info`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setShowFinalInfoForm(false);
      setFinalInfoData({ arrivalTime: '', arrivalDay: '', guides: '', sinister: '', comments: '', receptionBellPhoto: null });
      fetchRoute();
      alert('Información final enviada exitosamente. Esperando aprobación del administrador.');
    } catch (error) {
      alert(error.response?.data?.message || error.response?.data?.errors?.[0]?.msg || 'Error al enviar información final');
    } finally {
      setUploading(false);
    }
  };

  const handleReject = async () => {
    if (window.confirm('¿Estás seguro de que deseas rechazar esta ruta? Esta acción no se puede deshacer.')) {
      try {
        await axios.put(`/api/user/routes/${id}/reject`);
        fetchRoute();
      } catch (error) {
        alert(error.response?.data?.message || 'Error al rechazar la ruta');
      }
    }
  };

  const handleAddUpdate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`/api/user/routes/${id}/updates`, updateData);
      setShowUpdateForm(false);
      setUpdateData({ updateType: 'in-transit', description: '', location: '' });
      fetchRoute();
    } catch (error) {
      alert(error.response?.data?.message || 'Error al agregar actualización');
    }
  };

  const handleAdvance = async () => {
    if (window.confirm('¿Estás seguro de que deseas avanzar esta ruta?')) {
      try {
        await axios.put(`/api/user/routes/${id}/advance`);
        fetchRoute();
      } catch (error) {
        alert(error.response?.data?.message || 'Error al avanzar la ruta');
      }
    }
  };

  if (loading) {
    return <div className="loading">Cargando detalles de la ruta...</div>;
  }

  if (!route) {
    return <div className="card">Ruta no encontrada</div>;
  }

  const handlePhotoChange = (e) => {
    const files = Array.from(e.target.files);
    setWaybillData({
      ...waybillData,
      photos: files
    });
  };

  const handleWaybillSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('additionalComments', waybillData.additionalComments);
      waybillData.photos.forEach((photo) => {
        formData.append('photos', photo);
      });

      await axios.post(`/api/user/routes/${id}/waybill`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });
      setShowWaybillForm(false);
      setWaybillData({ additionalComments: '', photos: [] });
      fetchRoute();
      alert('¡Información de guía de ruta enviada exitosamente! Esperando aprobación del administrador.');
    } catch (error) {
      alert(error.response?.data?.message || 'Error al enviar información de guía de ruta');
    } finally {
      setUploading(false);
    }
  };

  // Helper function to extract user ID as string for comparison
  const getUserIdString = (userOrId) => {
    if (!userOrId) return null;
    // If it's a string, return it
    if (typeof userOrId === 'string') return userOrId;
    // If it's an object with _id (populated user), extract _id
    if (userOrId._id) {
      return typeof userOrId._id === 'string' ? userOrId._id : userOrId._id.toString();
    }
    // If it's an object with id (AuthContext format), extract id
    if (userOrId.id) {
      return typeof userOrId.id === 'string' ? userOrId.id : userOrId.id.toString();
    }
    // If it has toString method, use it
    if (typeof userOrId.toString === 'function') {
      return userOrId.toString();
    }
    // Last resort
    return String(userOrId);
  };

  // Get current user ID and assigned user ID as strings for comparison
  // Handle both _id (from API) and id (from AuthContext)
  const currentUserId = user ? (getUserIdString(user._id || user.id)) : null;
  const assignedUserId = route?.assignedTo ? getUserIdString(route.assignedTo) : null;
  
  // Compare IDs: if current user ID matches assigned user ID, user is accepted
  const isAssignedToCurrentUser = currentUserId && assignedUserId && currentUserId === assignedUserId;
  
  // Check if user is not selected (route is assigned to someone else)
  const isNotSelected = route?.assignedTo && currentUserId && assignedUserId && !isAssignedToCurrentUser;
  
  // Get display status - match the logic from UserRoutes.js list page
  let statusText;
  
  // Priority 1: If user is assigned (check both frontend comparison and backend flag), show status based on route status
  // Use backend flag if available, otherwise use frontend comparison
  const isAssigned = route.isAssignedToCurrentUser !== undefined ? route.isAssignedToCurrentUser : isAssignedToCurrentUser;
  
  if (isAssigned) {
    // If route is pending, show "Pending (Selected)"
    if (route.status === 'pending') {
      statusText = 'Pendiente (Seleccionada)';
    } else if (route.status === 'in-progress') {
      // If route is in-progress, show "En Progreso"
      statusText = 'En Progreso';
    } else {
      // For other statuses, use status map
      const statusMap = {
        'approved': 'Aprobada',
        'pending-acceptance': 'Pendiente Aceptación',
        'accepted': 'Aceptada',
        'pending-approval': 'Pendiente Aprobación',
        'completed': 'Completada',
        'rejected': 'Rechazada'
      };
      statusText = statusMap[route.status] || route.status || 'Aceptada';
    }
  }
  // Priority 2: If route has displayStatus from backend (for routes assigned to others)
  else if (route.displayStatus) {
    // Map display statuses to match list page formatting exactly
    if (route.displayStatus === 'not-selected') {
      statusText = 'No Seleccionada';
    } else if (route.displayStatus === 'assigned-to-other') {
      statusText = 'Asignada a Otro';
    } else if (route.displayStatus === 'rejected') {
      statusText = 'Rechazada';
    } else {
      statusText = route.displayStatus;
    }
  }
  // Priority 3: Use route status for unassigned routes
  else {
    const statusMap = {
      'pending': 'Pendiente',
      'approved': 'Aprobada',
      'pending-acceptance': 'Pendiente Aceptación',
      'accepted': 'Aceptada',
      'in-progress': 'En Progreso',
      'pending-approval': 'Pendiente Aprobación',
      'completed': 'Completada',
      'rejected': 'Rechazada',
      'returned': 'Devuelta'
    };
    statusText = statusMap[route.status] || route.status || 'Pendiente';
  }

  // Check if route status is "accepted" AND status text is NOT "Not Selected" - if yes, show all elements
  const isAcceptedStatus = route.status === 'accepted' && statusText !== 'No Seleccionada';
  
  // Only show these actions if route status is "accepted" and NOT "Not Selected"
  // When status is "in-progress" and initial info is submitted, show final info form instead
  const canAdvance = isAcceptedStatus;
  const canAddUpdate = isAcceptedStatus && statusText !== 'No Seleccionada';
  // Allow waybill submission if status is "accepted" or "returned" (for resubmission)
  const canSubmitWaybill = (isAcceptedStatus || route.status === 'returned') && statusText !== 'No Seleccionada' && isAssigned;
  
  // Show final info form when status is "in-progress", user is assigned, initial info is submitted, but final info is not submitted
  const canSubmitFinalInfo = route.status === 'in-progress' && isAssigned && route.initialInfo && !route.finalInfo;

  if (authLoading || loading) {
    return <div className="loading">Cargando detalles de la ruta...</div>;
  }

  if (!route) {
    return <div className="card">Ruta no encontrada</div>;
  }

  return (
    <div>
      <h2>Detalles de la Ruta: {route.routeNumber}</h2>
      
      <div className="card">
        <h3>Información Básica</h3>
        <p><strong>Destino:</strong> {route.endPoint}</p>
        {route.retreatDay && <p><strong>Día de Retiro:</strong> {new Date(route.retreatDay).toLocaleDateString('es-ES')}</p>}
        {route.wineryHours && <p><strong>Horario de Bodega:</strong> {route.wineryHours}</p>}
        {route.warehouseSchedule2 && <p><strong>Horario de Almacén 2:</strong> {route.warehouseSchedule2}</p>}
        {route.warehousePickupSchedule && <p><strong>Horario de Recogida en Almacén:</strong> {route.warehousePickupSchedule}</p>}
        {route.warehousePickupSchedule2 && <p><strong>Horario de Recogida en Almacén 2:</strong> {route.warehousePickupSchedule2}</p>}
        {route.arrivalAtDestinationDeliveryDay && <p><strong>Día de Llegada al Destino - Día de Entrega:</strong> {new Date(route.arrivalAtDestinationDeliveryDay).toLocaleDateString('es-ES')}</p>}
        <p><strong>Estado:</strong> <span className={(() => {
          // Map statusText to admin's badge classes
          if (statusText === 'Pendiente (Seleccionada)') return 'badge badge-pending';
          if (statusText === 'En Progreso') return 'badge badge-in-progress';
          if (statusText === 'Aceptada') return 'badge badge-accepted';
          if (statusText === 'Completada') return 'badge badge-completed';
          if (statusText === 'Rechazada' || statusText === 'No Seleccionada') return 'badge badge-rejected';
          if (statusText === 'Devuelta') return 'badge badge-warning';
          if (statusText === 'Pendiente Aprobación') return 'badge badge-pending-approval';
          if (statusText === 'Pendiente Aceptación') return 'badge badge-pending-acceptance';
          if (statusText === 'Aprobada') return 'badge badge-approved';
          if (statusText === 'Asignada a Otro') return 'badge badge-secondary';
          // Default to pending
          return 'badge badge-pending';
        })()} style={{ textTransform: 'none' }}>{statusText}</span></p>
        {statusText === 'No Seleccionada' && route.assignedTo && (
          <>
            <p><strong>Empresa del Usuario Seleccionado:</strong> {route.assignedTo.companyName || route.assignedTo.name || 'N/A'}</p>
            {route.driverName && (
              <p><strong>Nombre del Conductor Seleccionado:</strong> {route.driverName}</p>
            )}
          </>
        )}
      </div>

      {(isAcceptedStatus || (route.status === 'pending' && isAssignedToCurrentUser) || (route.status === 'in-progress' && isAssignedToCurrentUser)) && route.initialInfo && (
        <div className="card">
          <h3>Información Inicial Enviada</h3>
          <p><strong>Nombre del Conductor:</strong> {route.initialInfo.driverName}</p>
          <p><strong>RUT:</strong> {route.initialInfo.rut}</p>
          <p><strong>Patente del Tracto:</strong> {route.initialInfo.truckLicensePlate}</p>
          <p><strong>Patente de Rampla Celular:</strong> {route.initialInfo.trailerLicensePlate}</p>
          {route.initialInfo.comments && <p><strong>Comentarios:</strong> {route.initialInfo.comments}</p>}
          {route.initialInfo.submittedAt && <p><strong>Enviado:</strong> {new Date(route.initialInfo.submittedAt).toLocaleString('es-ES')}</p>}
        </div>
      )}

      {(route.status === 'in-progress' || route.status === 'pending-approval' || route.status === 'completed' || route.status === 'rejected') && route.finalInfo && (
        <div className="card">
          <h3>Información Final Enviada</h3>
          <p><strong>Hora de Llegada:</strong> {route.finalInfo.arrivalTime}</p>
          {route.finalInfo.arrivalDay && <p><strong>Día de Llegada:</strong> {new Date(route.finalInfo.arrivalDay).toLocaleDateString('es-ES')}</p>}
          <p><strong>Guías:</strong> {route.finalInfo.guides}</p>
          {route.finalInfo.sinister && <p><strong>Siniestro:</strong> {route.finalInfo.sinister}</p>}
          {route.finalInfo.comments && <p><strong>Comentarios:</strong> {route.finalInfo.comments}</p>}
          {route.finalInfo.receptionBellPhoto && (
            <div style={{ marginTop: '15px' }}>
              <p><strong>Foto de Timbre de Recepción:</strong></p>
              <img 
                src={`http://localhost:5000${route.finalInfo.receptionBellPhoto.path}`} 
                alt="Timbre de Recepción"
                style={{ maxWidth: '300px', height: 'auto', border: '1px solid #ddd', borderRadius: '4px', cursor: 'pointer' }}
                onClick={() => window.open(`http://localhost:5000${route.finalInfo.receptionBellPhoto.path}`, '_blank')}
              />
            </div>
          )}
          {route.finalInfo.submittedAt && <p style={{ marginTop: '15px' }}><strong>Enviado:</strong> {new Date(route.finalInfo.submittedAt).toLocaleString('es-ES')}</p>}
        </div>
      )}

      {/* Display admin feedback after final info is reviewed */}
      {route.adminFeedback && (route.status === 'completed' || route.status === 'rejected') && (
        <div className="card">
          <h3>Retroalimentación del Administrador</h3>
          <div style={{ padding: '15px', background: route.status === 'completed' ? '#e7f3ff' : '#fee2e2', borderRadius: '4px' }}>
            {route.adminFeedback.sinister && (
              <p style={{ marginBottom: '10px' }}>
                <strong>Siniestro:</strong> {route.adminFeedback.sinister}
              </p>
            )}
            {route.adminFeedback.comments && (
              <p style={{ marginBottom: '10px' }}>
                <strong>Comentarios:</strong> {route.adminFeedback.comments}
              </p>
            )}
            {route.adminFeedback.reviewedAt && (
              <p style={{ marginTop: '10px', fontSize: '14px', color: '#666' }}>
                <strong>Revisado:</strong> {new Date(route.adminFeedback.reviewedAt).toLocaleString('es-ES')}
                {route.adminFeedback.reviewedBy && route.adminFeedback.reviewedBy.name && (
                  <span> por {route.adminFeedback.reviewedBy.name}</span>
                )}
              </p>
            )}
            <p style={{ marginTop: '10px', fontWeight: 'bold', color: route.status === 'completed' ? '#065f46' : '#991b1b' }}>
              Estado: {route.status === 'completed' ? '✓ Ruta Completada' : '✗ Ruta Rechazada'}
            </p>
          </div>
        </div>
      )}

      {route.acceptanceRequests && route.acceptanceRequests.some(req => req.user?._id === user?._id && req.status === 'pending') && (
        <div className="card">
          <div className="alert" style={{ background: '#fff3cd', color: '#856404', border: '1px solid #ffeaa7' }}>
            <strong>Aprobación Pendiente del Administrador:</strong> Tu solicitud de aceptación está esperando la aprobación del administrador.
          </div>
        </div>
      )}
      
      {(() => {
        if (!route.acceptanceRequests || !user || !user._id) return null;
        
        const currentUserId = user._id.toString();
        
        // Find user's request
        const userRequest = route.acceptanceRequests.find(req => {
          const requestUserId = req.user?._id ? req.user._id.toString() : (req.user ? req.user.toString() : null);
          return requestUserId === currentUserId;
        });
        
        // If route is assigned to someone else and user has a request
        if (route.assignedTo) {
          const assignedUserId = route.assignedTo._id ? route.assignedTo._id.toString() : route.assignedTo.toString();
          if (assignedUserId !== currentUserId && userRequest) {
            const assignedUserName = route.assignedTo.name || 'Otro usuario';
            return (
              <div className="card">
                <div className="alert" style={{ background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' }}>
                  <strong>Otro Usuario Seleccionado:</strong> Esta ruta ha sido asignada a {assignedUserName}. {userRequest.rejectionReason || 'Tu solicitud no fue seleccionada.'}
                </div>
              </div>
            );
          }
        }
        
        // If user's request was rejected
        if (userRequest && userRequest.status === 'rejected') {
          return (
            <div className="card">
              <div className="alert" style={{ background: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' }}>
                <strong>Solicitud Rechazada:</strong> {userRequest.rejectionReason || 'Tu solicitud de aceptación ha sido rechazada. Otro usuario fue elegido para esta ruta.'}
              </div>
            </div>
          );
        }
        
        return null;
      })()}

      {/* Show initial info form if status is "Pendiente (Seleccionada)" and no initial info exists */}
      {(() => {
        // Must have user and route - check both _id and id for user
        if (!user || (!user._id && !user.id) || !route) {
          return null;
        }
        
        // If initial info already submitted, show message instead
        if (route.initialInfo) {
          return (
            <div className="card">
              <div className="alert" style={{ background: '#d1ecf1', color: '#0c5460', border: '1px solid #bee5eb' }}>
                <strong>Información Inicial Ya Enviada:</strong> Ya has enviado la información inicial para esta ruta.
              </div>
            </div>
          );
        }
        
        // Check if statusText is "Pendiente (Seleccionada)" OR check the same conditions that create this status
        // This ensures the form shows when the badge shows "Pendiente (Seleccionada)"
        const statusTextMatches = statusText === 'Pendiente (Seleccionada)';
        
        // Also check the conditions directly: route is pending AND user is assigned
        const backendIsAssigned = route.isAssignedToCurrentUser === true;
        const frontendIsAssigned = isAssignedToCurrentUser === true;
        const userIsAssigned = backendIsAssigned || frontendIsAssigned;
        const conditionsMatch = route.status === 'pending' && userIsAssigned;
        
        // Show form if either statusText matches OR conditions match
        const shouldShowForm = statusTextMatches || conditionsMatch;
        
        // Show form if status is "Pendiente (Seleccionada)"
        if (shouldShowForm) {
          return (
            <div className="card">
              <h3>Enviar Información Inicial</h3>
              <p style={{ marginBottom: '15px', color: '#666' }}>Completa la información requerida para iniciar esta ruta. Los campos marcados con * son obligatorios.</p>
              <form onSubmit={handleSubmitInitialInfo}>
                <div className="form-group">
                  <label>Nombre del Conductor *</label>
                  <input
                    type="text"
                    value={initialInfoData.driverName}
                    onChange={(e) => setInitialInfoData({ ...initialInfoData, driverName: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>RUT *</label>
                  <input
                    type="text"
                    value={initialInfoData.rut}
                    onChange={(e) => setInitialInfoData({ ...initialInfoData, rut: e.target.value })}
                    placeholder="Ej: 12345678-9"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Patente del Tracto *</label>
                  <input
                    type="text"
                    value={initialInfoData.truckLicensePlate}
                    onChange={(e) => setInitialInfoData({ ...initialInfoData, truckLicensePlate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Patente de Rampla Celular *</label>
                  <input
                    type="text"
                    value={initialInfoData.trailerLicensePlate}
                    onChange={(e) => setInitialInfoData({ ...initialInfoData, trailerLicensePlate: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Comentarios</label>
                  <textarea
                    value={initialInfoData.comments}
                    onChange={(e) => setInitialInfoData({ ...initialInfoData, comments: e.target.value })}
                    rows="4"
                  />
                </div>
                <button type="submit" className="btn btn-success">Enviar Información</button>
              </form>
            </div>
          );
        }
        
        return null;
      })()}

      {/* Keep the state-based form as fallback */}
      {showInitialInfoForm && (
        <div className="card">
          <h3>Enviar Información Inicial</h3>
          <p style={{ marginBottom: '15px', color: '#666' }}>Completa la información requerida para iniciar esta ruta. Los campos marcados con * son obligatorios.</p>
          <form onSubmit={handleSubmitInitialInfo}>
            <div className="form-group">
              <label>Nombre del Conductor *</label>
              <input
                type="text"
                value={initialInfoData.driverName}
                onChange={(e) => setInitialInfoData({ ...initialInfoData, driverName: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>RUT *</label>
              <input
                type="text"
                value={initialInfoData.rut}
                onChange={(e) => setInitialInfoData({ ...initialInfoData, rut: e.target.value })}
                placeholder="Ej: 12345678-9"
                required
              />
            </div>
            <div className="form-group">
              <label>Patente del Tracto *</label>
              <input
                type="text"
                value={initialInfoData.truckLicensePlate}
                onChange={(e) => setInitialInfoData({ ...initialInfoData, truckLicensePlate: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Patente de Rampla Celular *</label>
              <input
                type="text"
                value={initialInfoData.trailerLicensePlate}
                onChange={(e) => setInitialInfoData({ ...initialInfoData, trailerLicensePlate: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Comentarios</label>
              <textarea
                value={initialInfoData.comments}
                onChange={(e) => setInitialInfoData({ ...initialInfoData, comments: e.target.value })}
              />
            </div>
            <button type="submit" className="btn btn-success">Enviar Información</button>
            <button type="button" className="btn btn-secondary" onClick={() => setShowInitialInfoForm(false)} style={{ marginLeft: '10px' }}>
              Cancelar
            </button>
          </form>
        </div>
      )}

      {/* Show final info form when status is "in-progress" and initial info is submitted */}
      {canSubmitFinalInfo && (
        <div className="card">
          <h3>Enviar Información Final</h3>
          <p style={{ marginBottom: '15px', color: '#666' }}>Completa la información final de la ruta. Los campos marcados con * son obligatorios.</p>
          <form onSubmit={handleSubmitFinalInfo}>
            <div className="form-group">
              <label>Hora de Llegada *</label>
              <input
                type="time"
                value={finalInfoData.arrivalTime}
                onChange={(e) => setFinalInfoData({ ...finalInfoData, arrivalTime: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Día de Llegada *</label>
              <input
                type="date"
                value={finalInfoData.arrivalDay}
                onChange={(e) => setFinalInfoData({ ...finalInfoData, arrivalDay: e.target.value })}
                required
              />
            </div>
            <div className="form-group">
              <label>Guías *</label>
              <input
                type="text"
                value={finalInfoData.guides}
                onChange={(e) => setFinalInfoData({ ...finalInfoData, guides: e.target.value })}
                placeholder="Números de guías"
                required
              />
            </div>
            <div className="form-group">
              <label>Siniestro</label>
              <input
                type="text"
                value={finalInfoData.sinister}
                onChange={(e) => setFinalInfoData({ ...finalInfoData, sinister: e.target.value })}
                placeholder="Información sobre siniestros (opcional)"
              />
            </div>
            <div className="form-group">
              <label>Comentarios</label>
              <textarea
                value={finalInfoData.comments}
                onChange={(e) => setFinalInfoData({ ...finalInfoData, comments: e.target.value })}
                rows="4"
              />
            </div>
            <div className="form-group">
              <label style={{ 
                display: 'block', 
                marginBottom: '10px', 
                fontWeight: '600', 
                color: '#374151',
                fontSize: '14px'
              }}>
                Foto de Timbre de Recepción <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <div style={{
                position: 'relative',
                border: '2px dashed #d1d5db',
                borderRadius: '12px',
                padding: '24px',
                textAlign: 'center',
                backgroundColor: '#f9fafb',
                transition: 'all 0.3s ease',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = '#3b82f6';
                e.currentTarget.style.backgroundColor = '#eff6ff';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#d1d5db';
                e.currentTarget.style.backgroundColor = '#f9fafb';
              }}
              onClick={() => {
                const fileInput = document.getElementById('receptionBellPhotoInput');
                if (fileInput) fileInput.click();
              }}
              >
                <input
                  id="receptionBellPhotoInput"
                  type="file"
                  accept="image/*"
                  onChange={handleFinalInfoPhotoChange}
                  required
                  style={{ 
                    position: 'absolute',
                    width: '100%',
                    height: '100%',
                    top: 0,
                    left: 0,
                    opacity: 0,
                    cursor: 'pointer'
                  }}
                />
                {!finalInfoData.receptionBellPhoto ? (
                  <>
                    <div style={{ fontSize: '48px', marginBottom: '12px' }}>📷</div>
                    <p style={{ 
                      margin: '0 0 8px 0', 
                      color: '#374151', 
                      fontWeight: '600',
                      fontSize: '16px'
                    }}>
                      Click para seleccionar una foto
                    </p>
                    <p style={{ 
                      margin: 0, 
                      color: '#6b7280', 
                      fontSize: '14px'
                    }}>
                      o arrastra y suelta aquí
                    </p>
                    <p style={{ 
                      margin: '8px 0 0 0', 
                      color: '#9ca3af', 
                      fontSize: '12px'
                    }}>
                      PNG, JPG, GIF hasta 10MB
                    </p>
                  </>
                ) : (
                  <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '12px'
                  }}>
                    <div style={{
                      fontSize: '48px',
                      color: '#10b981'
                    }}>✓</div>
                    <div style={{
                      backgroundColor: '#d1fae5',
                      color: '#059669',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '14px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span>📄</span>
                      <span>{finalInfoData.receptionBellPhoto.name}</span>
                    </div>
                    <p style={{ 
                      margin: 0, 
                      color: '#6b7280', 
                      fontSize: '13px',
                      fontStyle: 'italic'
                    }}>
                      Click para cambiar la foto
                    </p>
                  </div>
                )}
              </div>
            </div>
            <button type="submit" className="btn btn-success" disabled={uploading}>
              {uploading ? 'Enviando...' : 'Enviar Información Final'}
            </button>
          </form>
        </div>
      )}

      {canAdvance && (
        <div className="card">
          <button className="btn btn-primary" onClick={handleAdvance}>
            Avanzar Estado de la Ruta
          </button>
        </div>
      )}

      {canSubmitWaybill && (
        <div className="card">
          <h3>Enviar Información de Guía de Ruta</h3>
          {route.status === 'returned' && route.adminFeedback && (
            <div className="alert" style={{ 
              background: '#fef3c7', 
              color: '#92400e', 
              border: '1px solid #fbbf24',
              marginBottom: '15px',
              padding: '12px 16px',
              borderRadius: '8px'
            }}>
              <strong>⚠️ Guía Devuelta:</strong> {route.adminFeedback.comments || 'La foto necesita ser corregida o reenviada'}
            </div>
          )}
          {!showWaybillForm ? (
            <div>
              <p>
                {route.status === 'returned' 
                  ? 'Por favor, corrige o reenvía las fotos según las indicaciones del administrador. Después del envío, la ruta será enviada nuevamente para aprobación.'
                  : 'Sube fotos y agrega comentarios para completar la ruta. Después del envío, la ruta será enviada para aprobación del administrador.'}
              </p>
              <button className="btn btn-primary" onClick={() => setShowWaybillForm(true)}>
                {route.status === 'returned' ? 'Reenviar Información de Guía de Ruta' : 'Enviar Información de Guía de Ruta'}
              </button>
            </div>
          ) : (
            <form onSubmit={handleWaybillSubmit}>
              <div className="form-group">
                <label style={{ 
                  display: 'block', 
                  marginBottom: '10px', 
                  fontWeight: '600', 
                  color: '#374151',
                  fontSize: '14px'
                }}>
                  Subir Fotos (Máximo 10 imágenes)
                </label>
                <div style={{
                  position: 'relative',
                  border: '2px dashed #d1d5db',
                  borderRadius: '12px',
                  padding: '24px',
                  textAlign: 'center',
                  backgroundColor: '#f9fafb',
                  transition: 'all 0.3s ease',
                  cursor: 'pointer'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.backgroundColor = '#eff6ff';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#d1d5db';
                  e.currentTarget.style.backgroundColor = '#f9fafb';
                }}
                onClick={() => {
                  const fileInput = document.getElementById('waybillPhotosInput');
                  if (fileInput) fileInput.click();
                }}
                >
                  <input
                    id="waybillPhotosInput"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handlePhotoChange}
                    style={{ 
                      position: 'absolute',
                      width: '100%',
                      height: '100%',
                      top: 0,
                      left: 0,
                      opacity: 0,
                      cursor: 'pointer'
                    }}
                  />
                  {waybillData.photos.length === 0 ? (
                    <>
                      <div style={{ fontSize: '48px', marginBottom: '12px' }}>📸</div>
                      <p style={{ 
                        margin: '0 0 8px 0', 
                        color: '#374151', 
                        fontWeight: '600',
                        fontSize: '16px'
                      }}>
                        Click para seleccionar fotos
                      </p>
                      <p style={{ 
                        margin: 0, 
                        color: '#6b7280', 
                        fontSize: '14px'
                      }}>
                        o arrastra y suelta aquí
                      </p>
                      <p style={{ 
                        margin: '8px 0 0 0', 
                        color: '#9ca3af', 
                        fontSize: '12px'
                      }}>
                        PNG, JPG, GIF hasta 10MB cada una
                      </p>
                    </>
                  ) : (
                    <div style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        fontSize: '48px',
                        color: '#10b981'
                      }}>✓</div>
                      <div style={{
                        backgroundColor: '#d1fae5',
                        color: '#059669',
                        padding: '8px 16px',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '14px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span>📷</span>
                        <span>{waybillData.photos.length} foto(s) seleccionada(s)</span>
                      </div>
                      <p style={{ 
                        margin: 0, 
                        color: '#6b7280', 
                        fontSize: '13px',
                        fontStyle: 'italic'
                      }}>
                        Click para cambiar las fotos
                      </p>
                    </div>
                  )}
                </div>
              </div>
              <div className="form-group">
                <label>Comentarios Adicionales</label>
                <textarea
                  value={waybillData.additionalComments}
                  onChange={(e) => setWaybillData({ ...waybillData, additionalComments: e.target.value })}
                  rows="4"
                  placeholder="Agrega cualquier comentario sobre incidentes o información importante durante la ruta..."
                />
              </div>
              <button type="submit" className="btn btn-success" disabled={uploading}>
                {uploading ? 'Enviando...' : 'Enviar Guía de Ruta'}
              </button>
              <button 
                type="button" 
                className="btn btn-secondary" 
                onClick={() => {
                  setShowWaybillForm(false);
                  setWaybillData({ additionalComments: '', photos: [] });
                }} 
                style={{ marginLeft: '10px' }}
              >
                Cancelar
              </button>
            </form>
          )}
        </div>
      )}

      {isAcceptedStatus && route.waybillInfo && route.waybillInfo.photos && route.waybillInfo.photos.length > 0 && (
        <div className="card">
          <h3>Fotos de la Guía de Ruta</h3>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '15px' }}>
            {route.waybillInfo.photos.map((photo, index) => (
              <div key={index} style={{ border: '1px solid #ddd', borderRadius: '4px', overflow: 'hidden' }}>
                <img 
                  src={`http://localhost:5000${photo.path}`} 
                  alt={`Guía de Ruta ${index + 1}`}
                  style={{ width: '100%', height: '150px', objectFit: 'cover', cursor: 'pointer' }}
                  onClick={() => window.open(`http://localhost:5000${photo.path}`, '_blank')}
                />
              </div>
            ))}
          </div>
          {route.waybillInfo.additionalComments && (
            <div style={{ marginTop: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
              <p><strong>Comentarios:</strong> {route.waybillInfo.additionalComments}</p>
              <small>Enviado: {new Date(route.waybillInfo.submittedAt).toLocaleString('es-ES')}</small>
            </div>
          )}
        </div>
      )}

      {canAddUpdate && (
        <div className="card">
          <h3>Actualizaciones del Viaje</h3>
          {!showUpdateForm ? (
            <button className="btn btn-primary" onClick={() => setShowUpdateForm(true)}>
              Agregar Actualización del Viaje
            </button>
          ) : (
            <form onSubmit={handleAddUpdate}>
              <div className="form-group">
                <label>Tipo de Actualización *</label>
                <select
                  value={updateData.updateType}
                  onChange={(e) => setUpdateData({ ...updateData, updateType: e.target.value })}
                  required
                >
                  <option value="started">Iniciado</option>
                  <option value="in-transit">En Tránsito</option>
                  <option value="delayed">Retrasado</option>
                  <option value="completed">Completado</option>
                  <option value="incident">Incidente</option>
                </select>
              </div>
              <div className="form-group">
                <label>Descripción *</label>
                <textarea
                  value={updateData.description}
                  onChange={(e) => setUpdateData({ ...updateData, description: e.target.value })}
                  required
                />
              </div>
              <div className="form-group">
                <label>Ubicación</label>
                <input
                  type="text"
                  value={updateData.location}
                  onChange={(e) => setUpdateData({ ...updateData, location: e.target.value })}
                />
              </div>
              <button type="submit" className="btn btn-success">Agregar Actualización</button>
              <button type="button" className="btn btn-secondary" onClick={() => setShowUpdateForm(false)} style={{ marginLeft: '10px' }}>
                Cancelar
              </button>
            </form>
          )}
        </div>
      )}

      {isAcceptedStatus && route.tripUpdates && route.tripUpdates.length > 0 && (
        <div className="card">
          <h3>Historial de Actualizaciones del Viaje</h3>
          {route.tripUpdates.map((update, index) => {
            const updateTypeMap = {
              'started': 'Iniciado',
              'in-transit': 'En Tránsito',
              'delayed': 'Retrasado',
              'completed': 'Completado',
              'incident': 'Incidente'
            };
            return (
              <div key={index} style={{ marginBottom: '15px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
                <p><strong>Tipo:</strong> {updateTypeMap[update.updateType] || update.updateType}</p>
                <p><strong>Descripción:</strong> {update.description}</p>
                {update.location && <p><strong>Ubicación:</strong> {update.location}</p>}
                <p><strong>Hora:</strong> {new Date(update.timestamp).toLocaleString('es-ES')}</p>
              </div>
            );
          })}
        </div>
      )}

      {route.comments && route.comments.length > 0 && (
        <div className="card">
          <h3>Comentarios</h3>
          {route.comments.map((comment, index) => (
            <div key={index} style={{ marginBottom: '10px', padding: '10px', background: '#f8f9fa', borderRadius: '4px' }}>
              <p>{comment.text}</p>
              <small>{new Date(comment.addedAt).toLocaleString('es-ES')}</small>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default UserRouteDetails;
